import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Banner } from '../../../DesignSystem/Feedback/Banner';
import { LoadingSpinner } from '../../../DesignSystem/Feedback/Icons/LoadingSpinner';
import { Modal } from '../../../DesignSystem/Feedback/Modal';
import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { Button } from '../../../DesignSystem/Inputs/Button';
import { HorizontalRule } from '../../../DesignSystem/Layout/HorizontalRule';
import { RewardPlaceholderImage } from '../../assets/images';

const messages = defineMessages({
  redeemReward: {
    defaultMessage: 'Redeem Reward',
    id: 'tTouHd',
  },
  selectGiftCardValue: {
    defaultMessage: 'Select gift card value',
    id: '0KEoIN',
  },
  cultureCardRedemptionPolicy: {
    defaultMessage: 'Culture card redemption policy',
    id: 'cTuUzu',
  },
  redeem: {
    defaultMessage: 'Redeem',
    id: 'XSdWHA',
  },
  close: {
    defaultMessage: 'Close',
    id: 'rbrahO',
  },
  checkEmail: {
    defaultMessage: 'Please check your email {email}',
    id: 'FK1I5l',
  },
  cultureRewardRedemptionInstructions: {
    defaultMessage:
      'Always check your spam if you do not see the reward. If you don’t receive an email to claim your reward, please notify your admin.',
    id: 'e89vpX',
  },
  rewardSecurityCode: {
    defaultMessage: 'Reward security code:',
    id: 'CgZiZK',
  },
  coordinateWithAdmin: {
    defaultMessage:
      'Your administrator, will contact you to coordinate your reward.',
    id: 'KR609M',
  },
  congratulations: {
    defaultMessage: 'Congratulations!',
    id: 'lOzDTt',
  },
  giftCard: {
    defaultMessage: 'Gift card',
    id: '4X0ZI8',
  },
  successfulRedemption: {
    defaultMessage: 'You have successfully redemeed a {name}',
    id: '6zX+5b',
  },
  redemptionInformation: {
    defaultMessage:
      'Once you click ‘Redeem’, your reward will be sent to {email}.',
    id: 'kniKwh',
  },
  redemptionRefundWarning: {
    defaultMessage:
      'IMPORTANT: After clicking ‘Redeem’, Assembly cannot issue a refund for this reward.',
    id: 'aF+zN4',
  },
  insufficientBalance: {
    defaultMessage: 'Insufficient balance to redeem.',
    id: '45Qnq3',
  },
  doneLabel: {
    defaultMessage: 'Done',
    id: 'JXdbo8',
  },
  cost: {
    defaultMessage: 'Cost',
    id: 'fBG/Ge',
  },
});

export type CultureRewardRedemptionInProgressProps = {
  rewardCardImage?: string;
  rewardName: string;
  rewardDescription: string;
  redemptionWarningBannerText?: string;
  onRedeemRewardClick: (id: string) => void;
  email: string;
  isCashOutRewardInProgress: boolean;
  cost: string;
  currencyIcon: ReactNode;
  id: string;
};

export type CultureRewardRedemptionSuccessfulProps = {
  rewardCardImage?: string;
  rewardName: string;
  rewardDescription: string;
  email: string;
  cost: string;
  currencyIcon: ReactNode;
  id: string;
};

export enum RedemptionModalState {
  Redeeming = 'redeeming',
  Redeemed = 'redeemed',
}

export type CultureRewardRedemptionModalProps = {
  isOpen: boolean;
  onClose: () => void;
} & (
  | ({
      state: RedemptionModalState.Redeeming;
    } & CultureRewardRedemptionInProgressProps)
  | ({
      state: RedemptionModalState.Redeemed;
    } & CultureRewardRedemptionSuccessfulProps)
);
export function CultureRewardRedemptionModal({
  isOpen,
  onClose,
  ...props
}: CultureRewardRedemptionModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={undefined}>
      {props.state === RedemptionModalState.Redeeming && (
        <CultureRewardRedemptionInProgress {...props} />
      )}
      {props.state === RedemptionModalState.Redeemed && (
        <CultureRewardRedemptionSuccessful
          {...props}
          handleRedemptionModalClose={onClose}
        />
      )}
    </Modal>
  );
}

function CultureRewardRedemptionInProgress({
  rewardCardImage,
  rewardName,
  rewardDescription,
  cost,
  currencyIcon,
  redemptionWarningBannerText,
  onRedeemRewardClick,
  email,
  isCashOutRewardInProgress,
  id,
}: CultureRewardRedemptionInProgressProps) {
  const { formatMessage } = useIntl();

  return (
    <div className="h-auto">
      <TextStyle variant="xl-medium">
        {formatMessage(messages.redeemReward)}
      </TextStyle>
      <div className="flex w-full flex-col items-center gap-2 p-4">
        <div className="flex w-full justify-center rounded-md">
          <img
            alt="Default placeholder for a Reward card"
            className="h-[125px] w-fit"
            src={
              rewardCardImage === '' || !rewardCardImage
                ? RewardPlaceholderImage
                : rewardCardImage
            }
          />
        </div>
        <TextStyle variant="base-medium">{rewardName}</TextStyle>
        <TextStyle variant="xs-regular">
          {formatMessage(messages.cost)}:
          <span>
            {currencyIcon} {cost}
          </span>
        </TextStyle>
      </div>
      <div className="rounded-lg bg-gray-3 p-4">
        <TextStyle variant="xs-regular">{rewardDescription}</TextStyle>
      </div>
      <HorizontalRule className="py-4" />
      <TextStyle variant="sm-medium">
        {formatMessage(messages.cultureCardRedemptionPolicy)}
      </TextStyle>
      <div className="max-h-[200px] overflow-scroll">
        <TextStyle className="pb-4" variant="xs-regular">
          {formatMessage(messages.redemptionInformation, { email })}
        </TextStyle>
        <TextStyle className="pb-4" variant="xs-regular">
          {formatMessage(messages.coordinateWithAdmin)}
        </TextStyle>
        <TextStyle className="pb-4" variant="xs-regular">
          {formatMessage(messages.redemptionRefundWarning)}
        </TextStyle>
      </div>
      <HorizontalRule className="pb-4" />
      {redemptionWarningBannerText && (
        <Banner className="rounded-xl" status="info">
          <TextStyle variant="xs-regular">
            {redemptionWarningBannerText}
          </TextStyle>
        </Banner>
      )}
      <div className="py-2">
        <Button
          onClick={() => {
            onRedeemRewardClick(id);
          }}
          isLoading={isCashOutRewardInProgress}
          className="w-full"
          variation="primary"
        >
          {formatMessage(messages.redeem)}
          {isCashOutRewardInProgress && <LoadingSpinner className="mr-2" />}
        </Button>
      </div>
    </div>
  );
}

function CultureRewardRedemptionSuccessful({
  rewardCardImage,
  rewardName,
  handleRedemptionModalClose,
  email,
}: CultureRewardRedemptionSuccessfulProps & {
  handleRedemptionModalClose: () => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <div className="h-auto">
      <TextStyle variant="lg-medium">
        {formatMessage(messages.congratulations)}
      </TextStyle>
      <div className="flex w-full flex-col items-center gap-2 py-4">
        <div className="flex w-full justify-center rounded-md">
          <img
            alt="Default placeholder for a Reward card"
            className="h-[125px] w-fit"
            src={rewardCardImage ?? RewardPlaceholderImage}
          />
        </div>
        <TextStyle variant="base-medium">
          {formatMessage(messages.successfulRedemption, {
            name: rewardName,
          })}
        </TextStyle>
      </div>
      <HorizontalRule className="py-4" />
      <TextStyle className="pb-4" variant="xs-regular">
        {formatMessage(messages.checkEmail, {
          email,
        })}
      </TextStyle>
      <TextStyle className="pb-4" variant="xs-regular">
        {formatMessage(messages.cultureRewardRedemptionInstructions)}
      </TextStyle>
      <HorizontalRule className="pb-4 pt-2" />
      <div className="py-2">
        <Button
          onClick={handleRedemptionModalClose}
          className="w-full"
          variation="secondaryEmphasized"
        >
          {formatMessage(messages.doneLabel)}
        </Button>
      </div>
    </div>
  );
}
